<script setup lang="ts">
import { NButton, NDropdown, NIcon, useDialog, type DataTableColumns } from 'naive-ui';
import { computed, h, ref, watch, type Component, type VNode } from 'vue';
import { RouterLink, useRouter, type RouteLocationRaw } from 'vue-router';
import type { AccessPermission } from '@/api';
import GaiaButton from '@/components/design/base/GaiaButton.vue';
import GaiaCheckbox from '@/components/design/base/GaiaCheckbox.vue';
import GaiaModal from '@/components/design/base/GaiaModal.vue';
import GaiaTable from '@/components/design/base/GaiaTable.vue';
import GaiaTag from '@/components/design/base/GaiaTag.vue';
import GaiaText from '@/components/design/base/GaiaText.vue';
import GaiaEmptyState from '@/components/design/composite/GaiaEmptyState.vue';
import {
  IconDelete,
  IconDocument,
  IconEdit,
  IconFolder,
  IconMore,
} from '@/components/design/icons';
import { useGaiaToast } from '@/composables/useGaiaToast';
import { vaultDirectoryRoute } from '@/router/links';
import { VaultShareService, type VaultShareTarget } from '@/services/vaultShareService';
import { ACCESS_PERMISSION_FLAGS, formatAccessPermissions } from '@/utils/accessPermission';

/**
 * The vault folders and plain files shared directly with one user or role —
 * the people pages' "Files & folders". A folder's share reaches everything
 * inside it, so a row may stand for much more than itself. Contracts have
 * their own tab.
 */
const props = defineProps<{
  subject: { kind: 'user' | 'role'; uid: string };
  /** "Ada Lovelace", "Legal team" — for the dialogs' wording. */
  subjectName: string;
  /** Offer change access and stop sharing (org admins). */
  manageable?: boolean;
}>();

const emit = defineEmits<{ count: [value: number] }>();

const router = useRouter();
const dialog = useDialog();
const message = useGaiaToast();

interface Row {
  key: string;
  target: VaultShareTarget & { kind: 'file' | 'directory' };
  name: string;
  permissions: AccessPermission;
}

const rows = ref<Row[]>([]);
const loading = ref(false);

async function load(): Promise<void> {
  const { kind, uid } = props.subject;
  loading.value = true;
  try {
    const [files, folders] =
      kind === 'user'
        ? await Promise.all([
            VaultShareService.getFileSharesByUser(uid),
            VaultShareService.getDirectorySharesByUser(uid),
          ])
        : await Promise.all([
            VaultShareService.getFileSharesByRole(uid),
            VaultShareService.getDirectorySharesByRole(uid),
          ]);
    if (props.subject.uid !== uid) return;
    rows.value = [
      ...folders.map(share => ({
        key: `dir-${share.vault_directory.uid}`,
        target: { kind: 'directory' as const, uid: share.vault_directory.uid },
        name: share.vault_directory.directory_name,
        permissions: share.permissions,
      })),
      ...files.map(share => ({
        key: `file-${share.vault_file.uid}`,
        target: { kind: 'file' as const, uid: share.vault_file.uid },
        name: share.vault_file.current_revision?.file_name ?? 'Untitled file',
        permissions: share.permissions,
      })),
    ];
  } catch (error) {
    message.error(error instanceof Error ? error.message : 'Failed to load shared files');
    rows.value = [];
  } finally {
    loading.value = false;
  }
}

function rowRoute(row: Row): RouteLocationRaw {
  return row.target.kind === 'directory'
    ? vaultDirectoryRoute(row.target.uid)
    : { name: 'vault-file-versions', params: { uid: row.target.uid } };
}

// ── Change access / stop sharing ────────────────────────────────────────────

const editing = ref<Row | null>(null);
const editBitmask = ref(0);
const saving = ref(false);

function openEdit(row: Row): void {
  editing.value = row;
  editBitmask.value = row.permissions;
}

function setFlag(flag: number, checked: boolean): void {
  editBitmask.value = checked ? editBitmask.value | flag : editBitmask.value & ~flag;
}

async function saveEdit(): Promise<void> {
  const row = editing.value;
  if (!row || editBitmask.value === 0 || saving.value) return;
  saving.value = true;
  try {
    await (props.subject.kind === 'user'
      ? VaultShareService.setUserShare(row.target, props.subject.uid, editBitmask.value)
      : VaultShareService.setRoleShare(row.target, props.subject.uid, editBitmask.value));
    message.success('Access updated');
    editing.value = null;
    await load();
  } catch (error) {
    message.error(error instanceof Error ? error.message : 'Failed to update access');
  } finally {
    saving.value = false;
  }
}

function confirmRemove(row: Row): void {
  dialog.warning({
    title: `Stop sharing ${row.name}?`,
    content:
      row.target.kind === 'directory'
        ? `${props.subjectName} loses the access this share gave them to the folder and everything inside it.`
        : `${props.subjectName} loses the access this share gave them.`,
    positiveText: 'Stop sharing',
    negativeText: 'Cancel',
    positiveButtonProps: { type: 'error' },
    onPositiveClick: async () => {
      try {
        await (props.subject.kind === 'user'
          ? VaultShareService.removeUserShare(row.target, props.subject.uid)
          : VaultShareService.removeRoleShare(row.target, props.subject.uid));
        message.success(`${row.name} is no longer shared with ${props.subjectName}`);
        await load();
      } catch (error) {
        message.error(error instanceof Error ? error.message : 'Failed to stop sharing');
      }
    },
  });
}

// ── Table ───────────────────────────────────────────────────────────────────

function menuIcon(icon: Component): () => VNode {
  return () => h(NIcon, null, { default: () => h(icon) });
}

const columns = computed((): DataTableColumns<Row> => [
  {
    title: 'Item',
    key: 'name',
    render: row =>
      h('div', { style: { display: 'flex', alignItems: 'center', gap: '8px', minWidth: 0 } }, [
        h(NIcon, {
          size: 16,
          component: row.target.kind === 'directory' ? IconFolder : IconDocument,
        }),
        h(
          RouterLink,
          {
            to: rowRoute(row),
            style: 'color: inherit; text-decoration: none; min-width: 0',
            onClick: (e: MouseEvent) => {
              e.stopPropagation();
            },
          },
          {
            default: () =>
              h(
                GaiaText,
                { size: 'body', weight: 'semibold', tone: 'ink', truncate: true },
                () => row.name
              ),
          }
        ),
        row.target.kind === 'directory'
          ? h(GaiaTag, { variant: 'muted', size: 'small' }, () => 'Folder')
          : null,
      ]),
  },
  {
    title: 'Access',
    key: 'permissions',
    width: 200,
    render: row =>
      h(GaiaTag, { variant: 'muted', size: 'small' }, () =>
        formatAccessPermissions(row.permissions)
      ),
  },
  {
    title: '',
    key: 'actions',
    width: 64,
    align: 'right',
    render: row =>
      props.manageable
        ? h(
            'span',
            {
              onClick: (e: MouseEvent) => {
                e.stopPropagation();
              },
            },
            [
              h(
                NDropdown,
                {
                  trigger: 'click',
                  placement: 'bottom-end',
                  options: [
                    { key: 'access', label: 'Change access', icon: menuIcon(IconEdit) },
                    { type: 'divider', key: 'divider' },
                    { key: 'remove', label: 'Stop sharing', icon: menuIcon(IconDelete) },
                  ],
                  onSelect: (key: string | number) => {
                    if (key === 'access') openEdit(row);
                    if (key === 'remove') confirmRemove(row);
                  },
                },
                {
                  default: () =>
                    h(
                      NButton,
                      {
                        quaternary: true,
                        size: 'small',
                        'aria-label': `Actions for ${row.name}`,
                        title: `Actions for ${row.name}`,
                      },
                      { icon: () => h(NIcon, null, { default: () => h(IconMore) }) }
                    ),
                }
              ),
            ]
          )
        : h('span'),
  },
]);

function rowProps(row: Row): Record<string, unknown> {
  return {
    style: { cursor: 'pointer' },
    onClick: (): void => {
      void router.push(rowRoute(row));
    },
  };
}

watch(() => props.subject.uid, load, { immediate: true });
watch(
  () => rows.value.length,
  count => {
    emit('count', count);
  },
  { immediate: true }
);
</script>

<template>
  <gaia-empty-state
    v-if="rows.length === 0 && !loading"
    title="No files or folders shared directly"
    dashed
  >
    <template #icon>
      <n-icon :component="IconFolder" :size="18" />
    </template>
    Folders and files shared with {{ subjectName }} show up here. A shared folder gives access to
    everything inside it.
  </gaia-empty-state>
  <template v-else>
    <gaia-text size="control" tone="subtle" tag="p" :style="{ margin: '0 0 12px' }">
      Shared with {{ subjectName }} directly. A shared folder reaches everything inside it.
    </gaia-text>
    <gaia-table
      :columns="columns"
      :data="rows"
      :loading="loading"
      :row-key="(row: Row) => row.key"
      :row-props="rowProps"
    />
  </template>

  <gaia-modal
    :show="editing !== null"
    width="400px"
    title="Change access"
    :mask-closable="!saving"
    :close-on-esc="!saving"
    @update:show="(value: boolean) => !value && (editing = null)"
  >
    <n-flex vertical :size="14" :wrap="false">
      <gaia-text size="body" tone="subtle" :style="{ lineHeight: 1.55 }">
        What {{ subjectName }} can do with {{ editing?.name }}.
      </gaia-text>
      <n-flex vertical :size="10" :wrap="false">
        <gaia-checkbox
          v-for="option in ACCESS_PERMISSION_FLAGS"
          :key="option.value"
          :checked="(editBitmask & option.value) !== 0"
          @update:checked="(checked: boolean) => setFlag(option.value, checked)"
        >
          {{ option.label }}
        </gaia-checkbox>
      </n-flex>
      <gaia-text v-if="editBitmask === 0" size="control" tone="subtle">
        Pick at least one, or use “Stop sharing” to remove access.
      </gaia-text>
    </n-flex>
    <template #action>
      <n-flex justify="end" :size="8" :wrap="false">
        <gaia-button :disabled="saving" @click="editing = null">Cancel</gaia-button>
        <gaia-button
          type="primary"
          :loading="saving"
          :disabled="editBitmask === 0 || editBitmask === editing?.permissions"
          @click="saveEdit"
        >
          Save access
        </gaia-button>
      </n-flex>
    </template>
  </gaia-modal>
</template>
